import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["results", "checkButton", "loading"]
  static values = { eventId: Number }

  connect() {
    this.resultsTarget.style.display = 'none'
  }

  check() {
    // Get selected learner IDs
    const learnersList = document.getElementById('evt-selected-learners-list')
    const userIds = Array.from(learnersList.querySelectorAll('li[data-id]'))
      .map(li => li.dataset.id)

    // Get selected scenario IDs
    const scenariosList = document.getElementById('evt-selected-list')
    const scenarioIds = Array.from(scenariosList.querySelectorAll('li[data-id]'))
      .map(li => li.dataset.id)

    if (userIds.length === 0 || scenarioIds.length === 0) {
      this.showMessage('Please select at least one learner and one scenario to check for prior ratings.', 'warning')
      return
    }

    // Show loading state
    this.checkButtonTarget.disabled = true
    this.checkButtonTarget.innerHTML = '<i class="fa fa-spinner fa-spin mr-1"></i> Checking...'

    // Make the request
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    fetch(`/events/${this.eventIdValue}/check_prior_ratings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        user_ids: userIds,
        scenario_ids: scenarioIds
      })
    })
    .then(response => response.json())
    .then(data => {
      this.displayResults(data)
    })
    .catch(error => {
      console.error('Error checking prior ratings:', error)
      this.showMessage('An error occurred while checking for prior ratings.', 'danger')
    })
    .finally(() => {
      this.checkButtonTarget.disabled = false
      this.checkButtonTarget.innerHTML = '<i class="fa fa-search mr-1"></i> Check for Prior Ratings'
    })
  }

  displayResults(data) {
    this.resultsTarget.style.display = 'block'

    // Inject styles if not already present
    if (!document.getElementById('prior-ratings-styles')) {
      const styleSheet = document.createElement('style')
      styleSheet.id = 'prior-ratings-styles'
      styleSheet.textContent = `
        .pr-container {
          border-radius: 8px;
          padding: 16px;
        }
        .pr-container.pr-success {
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
        }
        .pr-container.pr-warning {
          background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
          border: 1px solid #eab308;
          border-left: 4px solid #eab308;
        }
        .pr-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 12px;
        }
        .pr-header.pr-success-header { color: #065f46; }
        .pr-header.pr-success-header i { color: #10b981; }
        .pr-header.pr-warning-header { color: #854d0e; }
        .pr-header.pr-warning-header i { color: #ca8a04; }
        .pr-header i { font-size: 20px; }
        .pr-message { margin: 0; font-size: 14px; color: #047857; }
        .pr-summary {
          color: #713f12;
          font-weight: 500;
          margin-bottom: 16px;
          font-size: 13px;
          padding: 8px 12px;
          background: rgba(255,255,255,0.6);
          border-radius: 4px;
        }
        .pr-details {
          margin-top: 16px;
          max-height: 400px;
          overflow-y: auto;
        }
        .pr-user-group {
          background: white;
          border-radius: 8px;
          margin-bottom: 12px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          overflow: hidden;
        }
        .pr-user-header {
          font-weight: 600;
          color: white;
          font-size: 13px;
          padding: 10px 14px;
          background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
          display: flex;
          align-items: center;
        }
        .pr-user-header i { margin-right: 8px; opacity: 0.9; }
        .pr-badge {
          margin-left: auto;
          background: rgba(255,255,255,0.2);
          padding: 2px 10px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
        }
        .pr-list { list-style: none; padding: 0; margin: 0; }
        .pr-item {
          padding: 12px 14px;
          border-bottom: 1px solid #f3f4f6;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 8px;
          align-items: start;
        }
        .pr-item:last-child { border-bottom: none; }
        .pr-item:hover { background: #f9fafb; }
        .pr-scenario {
          font-weight: 600;
          color: #1f2937;
          font-size: 13px;
          display: flex;
          align-items: center;
        }
        .pr-scenario i { color: #6366f1; margin-right: 6px; font-size: 12px; }
        .pr-date {
          font-size: 11px;
          color: #6b7280;
          text-align: right;
          white-space: nowrap;
        }
        .pr-date i { margin-right: 4px; }
        .pr-meta {
          grid-column: 1 / -1;
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
          padding-top: 8px;
          border-top: 1px dashed #e5e7eb;
        }
        .pr-meta-item { display: inline-flex; align-items: center; }
        .pr-meta-item i { margin-right: 5px; font-size: 11px; color: #9ca3af; }
        .pr-meta-item strong { font-weight: 500; color: #4b5563; margin-right: 4px; }
      `
      document.head.appendChild(styleSheet)
    }

    if (!data.has_prior_ratings) {
      this.resultsTarget.innerHTML = `
        <div class="pr-container pr-success">
          <div class="pr-header pr-success-header">
            <i class="fa fa-check-circle"></i>
            <span>No Prior Ratings Found</span>
          </div>
          <p class="pr-message">None of the selected learners have been previously rated on the selected scenarios.</p>
        </div>
      `
      return
    }

    // Group ratings by user for display
    const byUser = this.groupBy(data.prior_ratings, 'user_name')
    
    let html = `
      <div class="pr-container pr-warning">
        <div class="pr-header pr-warning-header">
          <i class="fa fa-exclamation-triangle"></i>
          <span>Prior Ratings Found</span>
        </div>
        <p class="pr-summary">${data.summary}</p>
        <div class="pr-details">
    `

    for (const [userName, ratings] of Object.entries(byUser)) {
      // Group by scenario within user
      const byScenario = this.groupBy(ratings, 'scenario_name')
      const scenarioCount = Object.keys(byScenario).length
      
      html += `
        <div class="pr-user-group">
          <div class="pr-user-header">
            <i class="fa fa-user"></i> ${userName}
            <span class="pr-badge">${ratings.length} rating${ratings.length > 1 ? 's' : ''} on ${scenarioCount} scenario${scenarioCount > 1 ? 's' : ''}</span>
          </div>
          <ul class="pr-list">
      `
      
      ratings.forEach(rating => {
        html += `
          <li class="pr-item">
            <div class="pr-scenario">
              <i class="fa fa-bed"></i> ${rating.scenario_name}
            </div>
            <div class="pr-date">
              <i class="fa fa-calendar"></i> ${rating.rating_date}
            </div>
            <div class="pr-meta">
              <span class="pr-meta-item">
                <i class="fa fa-user-md"></i>
                <strong>Rater:</strong> ${rating.rater_name}
              </span>
              <span class="pr-meta-item">
                <i class="fa fa-calendar-check"></i>
                <strong>Event:</strong> ${rating.event_name}
              </span>
            </div>
          </li>
        `
      })

      html += `
          </ul>
        </div>
      `
    }

    html += `
        </div>
      </div>
    `

    this.resultsTarget.innerHTML = html
  }

  showMessage(message, type) {
    this.resultsTarget.style.display = 'block'
    this.resultsTarget.innerHTML = `
      <div class="prior-ratings-result ${type}">
        <div class="result-header">
          <i class="fa fa-${type === 'warning' ? 'exclamation-triangle' : 'times-circle'}"></i>
          <span>${message}</span>
        </div>
      </div>
    `
  }

  groupBy(array, key) {
    return array.reduce((result, item) => {
      const group = item[key]
      if (!result[group]) {
        result[group] = []
      }
      result[group].push(item)
      return result
    }, {})
  }
}
